import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2, MessageSquare, Send, User, Paperclip, X, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const tipos = [
  { value: "sugestao", label: "Sugestão", description: "Compartilhe ideias de melhoria" },
  { value: "reclamacao", label: "Reclamação", description: "Relate problemas ou insatisfações" },
  { value: "elogio", label: "Elogio", description: "Reconheça boas práticas" },
  { value: "informacao", label: "Solicitação de Informação", description: "Peça esclarecimentos" },
];

export default function OuvidoriaForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    tipo: "",
    nome: "",
    email: "",
    telefone: "",
    descricao: "",
    aceita_lgpd: false,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'mp3', 'mp4', 'wav'];
      if (!allowed.includes(ext)) {
        toast.error(`Arquivo ${file.name} não permitido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} muito grande (máx. 10MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.aceita_lgpd) {
      toast.error("É necessário aceitar a política de privacidade");
      return;
    }

    setLoading(true);
    try {
      // First create the ouvidoria
      const response = await axios.post(`${API_URL}/ouvidoria`, formData);
      const { protocolo } = response.data;

      // Then upload files if any
      if (files.length > 0) {
        for (const file of files) {
          const fileFormData = new FormData();
          fileFormData.append("protocolo", protocolo);
          fileFormData.append("file", file);
          
          try {
            await axios.post(`${API_URL}/ouvidoria/anexo`, fileFormData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            console.error("Error uploading file:", err);
          }
        }
      }

      navigate("/ouvidoria/confirmacao", { 
        state: { protocolo } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar solicitação");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.tipo &&
      formData.nome &&
      formData.email &&
      formData.telefone &&
      formData.descricao.length >= 20 &&
      formData.aceita_lgpd
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#1a1a2e] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-[#1a1a2e]">CONTERP</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 text-[#1a1a2e] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <MessageSquare className="w-4 h-4 text-[#f5a623]" />
            Canal Identificado
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            Ouvidoria
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Envie sugestões, reclamações, elogios ou solicite informações. 
            Seus dados serão tratados com sigilo.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            {/* Type Selection */}
            <div className="mb-8">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">
                Tipo de Solicitação *
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {tipos.map((tipo) => (
                  <button
                    key={tipo.value}
                    type="button"
                    onClick={() => handleChange("tipo", tipo.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 hover:-translate-y-0.5 ${
                      formData.tipo === tipo.value
                        ? "border-[#f5a623] bg-[#f5a623]/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`tipo-${tipo.value}`}
                  >
                    <span className="font-medium text-[#1a1a2e] block">{tipo.label}</span>
                    <span className="text-xs text-slate-500">{tipo.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-[#1a1a2e]" />
                <h3 className="font-semibold text-[#1a1a2e]">Dados de Contato</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="nome" className="text-sm font-medium text-slate-700">
                    Nome Completo *
                  </Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange("nome", e.target.value)}
                    placeholder="Seu nome"
                    className="mt-2"
                    data-testid="nome-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="seu@email.com"
                    className="mt-2"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="telefone" className="text-sm font-medium text-slate-700">
                    Telefone *
                  </Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => handleChange("telefone", e.target.value)}
                    placeholder="(00) 00000-0000"
                    className="mt-2"
                    data-testid="telefone-input"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <Label htmlFor="descricao" className="text-sm font-medium text-slate-700">
                Descrição *
              </Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => handleChange("descricao", e.target.value)}
                placeholder="Descreva sua solicitação com detalhes..."
                className="mt-2 min-h-[150px]"
                data-testid="descricao-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Mínimo de 20 caracteres ({formData.descricao.length}/20)
              </p>
            </div>

            {/* File Upload */}
            <div className="mb-8">
              <Label className="text-sm font-medium text-slate-700">
                Anexos (opcional)
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                Adicione documentos que possam ajudar no atendimento da sua solicitação
              </p>
              <div className="mt-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.mp3,.mp4,.wav"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-dashed border-2 py-6 hover:border-[#f5a623]"
                  data-testid="btn-anexar"
                >
                  <Upload className="w-5 h-5 mr-2 text-slate-400" />
                  <span className="text-slate-600">Clique para anexar arquivos</span>
                </Button>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  PDF, DOC, XLS, imagens, áudio (máx. 10MB cada)
                </p>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                    >
                      <Paperclip className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm text-[#1a1a2e] truncate">
                        {file.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* LGPD Consent */}
            <div className="flex items-start gap-3 mb-8 p-4 bg-slate-50 rounded-lg">
              <div className="flex-shrink-0 pt-0.5">
                <Checkbox
                  id="lgpd"
                  checked={formData.aceita_lgpd}
                  onCheckedChange={(checked) => handleChange("aceita_lgpd", checked)}
                  data-testid="lgpd-checkbox"
                />
              </div>
              <Label htmlFor="lgpd" className="text-sm text-slate-600 cursor-pointer select-none">
                Autorizo a coleta e tratamento dos meus dados pessoais para fins de atendimento 
                desta solicitação, conforme a{" "}
                <Link to="/politica-privacidade" className="text-[#0EA5E9] hover:underline" target="_blank">
                  Política de Privacidade
                </Link>
                {" "}e a Lei Geral de Proteção de Dados (LGPD).
              </Label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={!isFormValid() || loading}
              className="w-full bg-[#f5a623] hover:bg-[#f5a623]/90 text-[#1a1a2e] py-6"
              data-testid="btn-enviar"
            >
              {loading ? (
                "Enviando..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Solicitação
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
